import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, Stack, LinearProgress, Button, Skeleton } from '@mui/material';
import {
  RequestQuote, Assignment, LocalShipping, ReceiptLong, Payments, Groups, AccountBalanceWallet,
  Percent, PendingActions, Storefront, TrendingUp, WarningAmber, ArrowForwardIos, ChevronRight,
  ErrorOutline,
} from '@mui/icons-material';
import { api } from '../../services/apiClient';
import { formatIndianNumber } from '../../utils/currency';
import { useControlRoomColors } from '../../components/common/controlRoom';
import { RouterLink } from '../../utils/linkNav';

// ---------------------------------------------------------------------------
// API shapes (loosely typed — the backend is the source of truth; these just
// keep the JSX honest about what fields are expected).
// ---------------------------------------------------------------------------

interface KpiTile {
  key: string;
  label: string;
  value: number | null;
  money?: boolean;
  unit?: string;
  route: string;
  tone?: string;
}

interface FlowStage {
  key: string;
  label: string;
  route: string;
  count: number;
}

interface SpendCategory {
  category: string;
  value: number;
}

interface TopSupplier {
  id: number | string;
  name: string;
  spend: number;
  bills: number;
}

interface CommandCenterData {
  identity?: { company?: string; date?: string };
  kpis?: KpiTile[];
  flow?: FlowStage[];
  spendByCategory?: SpendCategory[];
  topSuppliers?: TopSupplier[];
}

interface ExceptionRow {
  id: number | string;
  kind: string;
  severity: 'critical' | 'warning';
  title: string;
  detail: string;
  impact: number | null;
  age: number;
  owner: string;
  suggestedAction: string;
  route: string;
}

interface ExceptionsData {
  summary?: { critical: number; warning: number; total: number };
  exceptions?: ExceptionRow[];
}

const ProcurementCommandCenter: React.FC = () => {
  const navigate = useNavigate();
  const C = useControlRoomColors();
  const [d, setD] = useState<CommandCenterData | null>(null);
  const [loading, setLoading] = useState(true);
  const [ex, setEx] = useState<ExceptionsData | null>(null);
  const [exLoading, setExLoading] = useState(true);

  useEffect(() => {
    api.get<CommandCenterData>('/procurement-analytics/command-center')
      .then(setD)
      .catch(() => setD(null))
      .finally(() => setLoading(false));
    api.get<ExceptionsData>('/procurement-analytics/exceptions')
      .then(setEx)
      .catch(() => setEx(null))
      .finally(() => setExLoading(false));
  }, []);

  // Tone → palette colour. Tiles arrive with a free-text "tone" hint from the
  // backend, so this maps the common vocabulary and always falls back to the
  // accent colour rather than a hardcoded hex.
  const toneColor = (tone?: string): string => {
    switch ((tone || '').toLowerCase()) {
      case 'good':
      case 'success':
      case 'positive':
        return C.good;
      case 'warn':
      case 'warning':
        return C.warn;
      case 'bad':
      case 'error':
      case 'danger':
      case 'negative':
        return C.bad;
      case 'purple':
      case 'secondary':
        return C.purple;
      case 'cyan':
      case 'info':
        return C.cyan;
      default:
        return C.accent;
    }
  };

  // Icon per KPI, matched loosely on the key so new tiles the backend adds
  // still get something sensible instead of nothing at all.
  const kpiIcon = (key: string): React.ReactNode => {
    const k = key.toLowerCase();
    if (k.includes('req')) return <RequestQuote />;
    if (k.includes('order')) return <Assignment />;
    if (k.includes('receipt') || k.includes('grn')) return <LocalShipping />;
    if (k.includes('invoice') || k.includes('bill')) return <ReceiptLong />;
    if (k.includes('payment') || k.includes('paid') || k.includes('payable')) return <Payments />;
    if (k.includes('vendor') || k.includes('supplier')) return <Groups />;
    if (k.includes('spend') || k.includes('value') || k.includes('cash')) return <AccountBalanceWallet />;
    if (k.includes('gst') || k.includes('tax') || k.includes('tds')) return <Percent />;
    if (k.includes('pending') || k.includes('open') || k.includes('due')) return <PendingActions />;
    if (k.includes('exception') || k.includes('overdue') || k.includes('late')) return <WarningAmber />;
    if (k.includes('trend') || k.includes('growth')) return <TrendingUp />;
    return <Storefront />;
  };

  const fmtVal = (t: KpiTile): string => {
    if (t.value === null || t.value === undefined) return '—';
    if (t.money) return formatIndianNumber(t.value);
    return `${t.value.toLocaleString('en-IN')}${t.unit || ''}`;
  };

  const sevIcon = (s: string) =>
    s === 'critical' ? <ErrorOutline sx={{ color: C.bad }} /> : <WarningAmber sx={{ color: C.warn }} />;

  const kpis = d?.kpis || [];
  const flow = d?.flow || [];
  const spendByCategory = d?.spendByCategory || [];
  const topSuppliers = d?.topSuppliers || [];
  const exceptions = (ex?.exceptions || []).slice(0, 4);
  const maxSpend = Math.max(1, ...spendByCategory.map((s) => s.value));

  return (
    <Box sx={{ bgcolor: C.bg, minHeight: '100%', m: -3, p: 0, color: C.text }}>
      {/* Identity bar */}
      <Box
        sx={{
          px: 3, py: 2, borderBottom: `1px solid ${C.line}`,
          background: `linear-gradient(90deg, ${C.panel}, ${C.bg})`,
          display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1,
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: C.good, boxShadow: `0 0 10px ${C.good}`, animation: 'pcc-pulse 2s infinite' }} />
          <Box>
            <Typography sx={{ fontWeight: 800, fontSize: 20, letterSpacing: 0.3 }}>{d?.identity?.company || 'Procurement Command Center'}</Typography>
            <Typography sx={{ color: C.sub, fontSize: 13 }}>Source-to-pay, live</Typography>
          </Box>
        </Stack>
        <Stack alignItems="flex-end">
          <Typography sx={{ fontFamily: 'monospace', fontSize: 15, color: C.text }}>{d?.identity?.date || ''}</Typography>
          <Typography sx={{ color: C.sub, fontSize: 12, textTransform: 'uppercase', letterSpacing: 1 }}>Live Procurement</Typography>
        </Stack>
        <style>{`@keyframes pcc-pulse{0%{opacity:1}50%{opacity:.35}100%{opacity:1}}`}</style>
      </Box>

      {loading && <LinearProgress />}

      {/* Hero KPI grid */}
      <Box sx={{ px: 3, py: 2.5, display: 'grid', gridTemplateColumns: { xs: 'repeat(2,1fr)', sm: 'repeat(3,1fr)', md: 'repeat(4,1fr)', lg: 'repeat(5,1fr)' }, gap: 1.5 }}>
        {loading
          ? Array.from({ length: 10 }).map((_, i) => (
              <Skeleton key={i} variant="rounded" height={84} sx={{ borderRadius: 2 }} />
            ))
          : kpis.map((t) => {
              const col = toneColor(t.tone);
              return (
                <Box
                  key={t.key}
                  onClick={() => t.route && navigate(t.route)}
                  sx={{
                    cursor: t.route ? 'pointer' : 'default', bgcolor: C.panel, border: `1px solid ${C.line}`,
                    borderRadius: 2, p: 2, transition: 'all .15s',
                    '&:hover': t.route ? { borderColor: col, transform: 'translateY(-2px)' } : {},
                  }}
                >
                  <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                    <Typography sx={{ color: C.sub, fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.6 }}>{t.label}</Typography>
                    <Box sx={{ color: col, opacity: 0.85, '& svg': { fontSize: 18 } }}>{kpiIcon(t.key)}</Box>
                  </Stack>
                  <Typography sx={{ fontWeight: 800, fontSize: 22, mt: 0.5, color: col, fontFamily: t.money ? 'inherit' : 'monospace' }}>{fmtVal(t)}</Typography>
                </Box>
              );
            })}
        {!loading && !kpis.length && (
          <Box sx={{ gridColumn: '1 / -1', textAlign: 'center', py: 3 }}>
            <Typography sx={{ color: C.sub, fontSize: 13 }}>No procurement metrics available yet.</Typography>
          </Box>
        )}
      </Box>

      {/* Source-to-pay flow */}
      <Box sx={{ px: 3, pb: 1 }}>
        <Typography sx={{ color: C.sub, fontSize: 11, textTransform: 'uppercase', letterSpacing: 1, mb: 1 }}>Source-to-Pay Flow · click a stage to enter it</Typography>
        <Box sx={{ display: 'flex', alignItems: 'stretch', gap: 0.5, overflowX: 'auto', pb: 1 }}>
          {flow.map((s, i) => (
            <React.Fragment key={s.key}>
              <Box
                onClick={() => navigate(s.route)}
                sx={{
                  cursor: 'pointer', minWidth: 120, bgcolor: C.panel2, border: `1px solid ${C.line}`, borderRadius: 1.5,
                  px: 1.5, py: 1, textAlign: 'center', transition: 'all .15s',
                  '&:hover': { borderColor: C.accent, bgcolor: C.panel },
                }}
              >
                <Typography sx={{ fontSize: 22, fontWeight: 800, color: s.count > 0 ? C.text : C.sub, fontFamily: 'monospace' }}>{s.count}</Typography>
                <Typography sx={{ fontSize: 11, color: C.sub }}>{s.label}</Typography>
              </Box>
              {i < flow.length - 1 && (
                <Box sx={{ display: 'flex', alignItems: 'center', color: C.line }}>
                  <ArrowForwardIos sx={{ fontSize: 12 }} />
                </Box>
              )}
            </React.Fragment>
          ))}
          {!loading && !flow.length && <Typography sx={{ color: C.sub, fontSize: 13, py: 2 }}>Flow data unavailable.</Typography>}
        </Box>
      </Box>

      {/* Spend by category + Top suppliers */}
      <Box sx={{ px: 3, py: 2, display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
        <Box sx={{ bgcolor: C.panel, border: `1px solid ${C.line}`, borderRadius: 2, p: 2 }}>
          <Typography sx={{ fontWeight: 700, fontSize: 14, mb: 1.5 }}>Spend by Category</Typography>
          <Stack spacing={1.25}>
            {spendByCategory.map((s) => (
              <Box key={s.category}>
                <Stack direction="row" justifyContent="space-between" alignItems="baseline">
                  <Typography sx={{ fontSize: 13, color: C.text }}>{s.category}</Typography>
                  <Typography sx={{ fontSize: 13, fontWeight: 700, color: C.text }}>{formatIndianNumber(s.value)}</Typography>
                </Stack>
                <Box sx={{ height: 6, borderRadius: 3, bgcolor: C.panel2, overflow: 'hidden', mt: 0.5 }}>
                  <Box sx={{ width: `${Math.min((s.value / maxSpend) * 100, 100)}%`, height: '100%', borderRadius: 3, bgcolor: C.accent, transition: 'width .4s cubic-bezier(.16,1,.3,1)' }} />
                </Box>
              </Box>
            ))}
            {!loading && !spendByCategory.length && <Typography sx={{ color: C.sub, fontSize: 13, py: 2, textAlign: 'center' }}>No spend recorded yet.</Typography>}
          </Stack>
        </Box>

        <Box sx={{ bgcolor: C.panel, border: `1px solid ${C.line}`, borderRadius: 2, p: 2 }}>
          <Typography sx={{ fontWeight: 700, fontSize: 14, mb: 1.5 }}>Top Suppliers</Typography>
          <Stack spacing={1}>
            {topSuppliers.map((s) => (
              <Box key={s.id} component={RouterLink} to={`/procurement/vendors/${s.id}`} sx={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 1.25, textDecoration: 'none', color: 'inherit', '&:hover': { color: C.accent } }}>
                <LocalShipping sx={{ color: C.sub, fontSize: 18 }} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ fontSize: 13 }} noWrap>{s.name}</Typography>
                  <Typography sx={{ fontSize: 11, color: C.sub }}>{s.bills} bills</Typography>
                </Box>
                <Typography sx={{ fontSize: 13, fontWeight: 700, color: C.good }}>{formatIndianNumber(s.spend)}</Typography>
              </Box>
            ))}
            {!loading && !topSuppliers.length && <Typography sx={{ color: C.sub, fontSize: 13, py: 2, textAlign: 'center' }}>No supplier spend yet.</Typography>}
          </Stack>
        </Box>
      </Box>

      {/* Needs attention preview */}
      <Box sx={{ px: 3, pb: 3 }}>
        <Box sx={{ bgcolor: C.panel, border: `1px solid ${C.line}`, borderRadius: 2, p: 2 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
            <Typography sx={{ fontWeight: 700, fontSize: 14 }}>Needs Attention</Typography>
            <Stack direction="row" spacing={1.5} alignItems="center">
              {!!ex?.summary && (
                <Typography sx={{ fontSize: 12, color: C.sub }}>{ex.summary.critical} critical · {ex.summary.warning} warning</Typography>
              )}
              <Button size="small" endIcon={<ChevronRight />} onClick={() => navigate('/procurement/exceptions')} sx={{ color: C.accent, textTransform: 'none' }}>View all</Button>
            </Stack>
          </Stack>
          <Stack spacing={1}>
            {exceptions.map((a) => (
              <Box key={a.id} sx={{ display: 'flex', alignItems: 'center', gap: 1.25, py: 1, px: 1, borderRadius: 1, bgcolor: C.panel2, border: `1px solid ${C.line}` }}>
                <Box sx={{ '& svg': { fontSize: 20 } }}>{sevIcon(a.severity)}</Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ fontSize: 13, fontWeight: 600, color: C.text }} noWrap>{a.title}</Typography>
                  <Typography sx={{ fontSize: 11, color: C.sub }} noWrap>{a.detail}</Typography>
                </Box>
                {a.suggestedAction && (
                  <Button size="small" endIcon={<ChevronRight />} onClick={() => navigate(a.route)} sx={{ color: C.accent, textTransform: 'none', whiteSpace: 'nowrap' }}>{a.suggestedAction}</Button>
                )}
              </Box>
            ))}
            {!exLoading && !exceptions.length && <Typography sx={{ color: C.good, fontSize: 13, py: 2, textAlign: 'center' }}>All clear — no exceptions.</Typography>}
          </Stack>
        </Box>
      </Box>
    </Box>
  );
};

export default ProcurementCommandCenter;
